import Grid2 from '@mui/material/Grid2'
import { find, findIndex, has, keys, map, pathOr, propEq, reduce, update } from 'ramda'
import React from 'react'

import Checkbox from '@/app/component/atom/form/checkbox'
import { MESSAGE_TYPES } from '@/app/module/campaigns/definitions'
import { mapIndexed } from '@/app/module/campaigns/helpers'
import type { CampaignItem, CampaignSummary, Part, PartSummary } from '@/app/module/campaigns/types'
import { getPartContentPreview } from '@/app/module/campaigns/utils/part-content'

import MessageDetails, { type MessageDetailsChartDatum, type MessageDetailsProps } from './components/message-detail'

type Props = {
  item?: CampaignItem
  itemSummary?: CampaignSummary
}

const AnalyticsTab: React.FC<Props> = (props) => {
  const [showLabels, setShowLabels] = React.useState(true)
  const content = processContentForSummary(props, showLabels)

  return (
    <Grid2
      container
      spacing={2}
      style={{
        marginBottom: '30px',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          width: '100%',
        }}
      >
        <Checkbox
          editable
          label="Show answer labels where available"
          name="show-answer-labels"
          value={String(showLabels)}
          onChange={({ value }) => setShowLabels(value === 'true')}
        />
      </div>
      <Grid2 size={{ md: 12 }}>
        {content.map(({ chartData, icon, id, isQuestion, message, name, total }) => (
          <MessageDetails
            key={id}
            chartData={chartData}
            icon={icon}
            id={id}
            isQuestion={isQuestion}
            message={message}
            name={name}
            total={total}
          />
        ))}
      </Grid2>
    </Grid2>
  )
}

const iconMap: Record<string, string> = {
  smsMessage: 'sms',
  smsQuestion: 'question_answer',
  voiceMessage: 'phone',
  voiceQuestion: 'ring_volume',
  actionsOnly: 'account_tree',
  quotaActions: 'fact_check',
  whatsappMessage: 'sms',
  whatsappQuestion: 'question_answer',
  catiMessage: 'sms',
  catiQuestion: 'question_answer',
}

const isQuestionMap: Record<string, boolean> = {
  smsMessage: false,
  smsQuestion: true,
  voiceMessage: false,
  voiceQuestion: true,
  quotaActions: true,
  whatsappMessage: false,
  whatsappQuestion: true,
  catiMessage: false,
  catiQuestion: true,
}

const processContentForPieChartWithLabels = (part?: PartSummary): MessageDetailsChartDatum[] =>
  reduce(
    (accum, key) => {
      const label = pathOr(key, ['replies', 'answerLabels', key], part)
      const answerIndex = findIndex(propEq('key', label), accum)
      if (answerIndex > -1) {
        const updatedValue = accum[answerIndex].value + pathOr(0, ['replies', 'answers', key], part)
        const updatePercentage = (updatedValue / pathOr(1, ['replies', 'valid'], part)) * 100
        return update(
          answerIndex,
          {
            ...accum[answerIndex],
            value: updatedValue,
            percentage: `${updatePercentage.toFixed(0)}%`,
            legendLabel: `${label} (${updatedValue} - ${updatePercentage.toFixed(0)}%)`,
          },
          accum,
        )
      }
      const value = pathOr(0, ['replies', 'answers', key], part)
      const percentage = (value / pathOr(1, ['replies', 'valid'], part)) * 100
      return [
        ...accum,
        {
          key: label,
          value,
          percentage: `${percentage.toFixed(0)}%`,
          legendLabel: `${label} (${value} - ${percentage.toFixed(0)}%)`,
        },
      ]
    },
    [] as MessageDetailsChartDatum[],
    keys(pathOr({}, ['replies', 'answers'], part)),
  )

const processContentForPieChart = (part?: PartSummary): MessageDetailsChartDatum[] =>
  map(
    (key) => {
      const value = pathOr(0, ['replies', 'answers', key], part)
      const percentage = (value / pathOr(1, ['replies', 'valid'], part)) * 100
      return {
        key,
        value,
        percentage: `${percentage.toFixed(0)}%`,
        legendLabel: `${key} (${value} - ${percentage.toFixed(0)}%)`,
      }
    },
    keys(pathOr({}, ['replies', 'answers'], part)),
  )

const selectContent = (
  message: Part,
  index: number,
  itemSummary: CampaignSummary | undefined,
  showLabels: boolean,
): MessageDetailsProps =>
  reduce(
    (accum, type) => {
      if (has(type.key, message)) {
        const partStats = itemSummary?.partStats || []
        const partStat = find(propEq('id', message.id), partStats)
        return {
          message: getPartContentPreview(message),
          icon: iconMap[type.key],
          isQuestion: isQuestionMap[type.key],
          id: message.id,
          name: `${index + 1}. ${message.label || type.label}`,
          chartData: showLabels ? processContentForPieChartWithLabels(partStat) : processContentForPieChart(partStat),
          total: pathOr(0, ['replies', 'valid'], partStat),
        }
      }
      return accum
    },
    {} as MessageDetailsProps,
    MESSAGE_TYPES,
  )

const processContentForSummary = ({ item, itemSummary }: Props, showLabels: boolean) =>
  mapIndexed(
    (message: Part, index: number) => selectContent(message, index, itemSummary, showLabels),
    pathOr([], ['variables', 'parts'], item),
  )

export default AnalyticsTab
